import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { tap } from 'rxjs';
import { PROGRESS_STATUSES } from 'symbols';
import { InstructionsService } from '../instructions.service';
import { InstructionNodeModel } from '../symbols';
import {
  CreateInstructionDirectory,
  DeleteInstructionNode,
  GetInstructions,
  RenameInstructionNode,
  UploadInstructionFile,
} from './instructions.actions';
import { InstructionsStateModel } from './instructions.model';

const DEFAULTS: InstructionsStateModel = {
  nodes: [],
  loadStatus: PROGRESS_STATUSES.NOT_INITIALIZED,
};

@State<InstructionsStateModel>({ name: 'Instructions', defaults: DEFAULTS })
@Injectable()
export class InstructionsState {
  constructor(private service: InstructionsService) {}

  @Selector()
  static nodes(state: InstructionsStateModel): InstructionNodeModel[] {
    return state.nodes;
  }

  @Selector()
  static loadStatus(state: InstructionsStateModel): string {
    return state.loadStatus;
  }

  @Action(GetInstructions)
  getInstructions(ctx: StateContext<InstructionsStateModel>) {
    ctx.patchState({ loadStatus: PROGRESS_STATUSES.IN_PROGRESS });
    return this.service.getTree().pipe(
      tap({
        next: nodes => ctx.patchState({ nodes, loadStatus: PROGRESS_STATUSES.SUCCESS }),
        error: () => ctx.patchState({ loadStatus: PROGRESS_STATUSES.FAIL }),
      }),
    );
  }

  @Action(CreateInstructionDirectory)
  createDirectory(ctx: StateContext<InstructionsStateModel>, { name, parentId }: CreateInstructionDirectory) {
    return this.service.createDirectory(name, parentId).pipe(
      tap(node => ctx.patchState({ nodes: [...ctx.getState().nodes, node] })),
    );
  }

  @Action(UploadInstructionFile)
  uploadFile(ctx: StateContext<InstructionsStateModel>, { directoryId, file }: UploadInstructionFile) {
    return this.service.uploadFile(directoryId, file).pipe(
      tap(node => ctx.patchState({ nodes: [...ctx.getState().nodes, node] })),
    );
  }

  @Action(RenameInstructionNode)
  renameNode(ctx: StateContext<InstructionsStateModel>, { id, name }: RenameInstructionNode) {
    return this.service.renameNode(id, name).pipe(
      tap(updated => ctx.patchState({
        nodes: ctx.getState().nodes.map(n => n.id === id ? updated : n),
      })),
    );
  }

  @Action(DeleteInstructionNode)
  deleteNode(ctx: StateContext<InstructionsStateModel>, { id }: DeleteInstructionNode) {
    return this.service.deleteNode(id).pipe(
      tap(() => {
        const removeIds = new Set<string>();
        const collectIds = (nodeId: string) => {
          removeIds.add(nodeId);
          ctx.getState().nodes
            .filter(n => n.parentId === nodeId)
            .forEach(n => collectIds(n.id));
        };
        collectIds(id);
        ctx.patchState({ nodes: ctx.getState().nodes.filter(n => !removeIds.has(n.id)) });
      }),
    );
  }
}
